import { _, actions, helpers, hooks, React, ts, useDispatch, useSelector } from '_core';

import { useAnalysesContext } from '_contexts/analyses-context';

import * as utils from './risk-model-utils';

type ProviderProps = {
  id: number;
  children: React.ReactNode;
};
type RiskModelContextTypes = {
  alert: ts.types.common.Alert;
  archivedDeps: ts.types.common.ArchivedDeps;
  definition: ts.types.riskModel.DefinitionDraft & { noValidate?: boolean };
  definitionChanged: boolean;
  depsTimestamp: {
    dependency: string;
    timestamp: any;
  }[];
  isExternalRiskModel: boolean;
  keepCurrent: () => Promise<void>;
  loading: boolean;
  resources: ts.StoreState['resources'];
  uiMetadata: ts.types.riskModel.UiMetaData;
  updateUiMetadata: (_m?: ts.types.riskModel.UiMetaData) => Promise<void>;
  updateUserPreferences: (_p: Partial<ts.types.riskModel.UserPreferences['preferences']>) => Promise<void>;
  userPreferences: ts.types.riskModel.UserPreferences['preferences'] | undefined;
  riskModel: ts.types.riskModel.RiskModelDraft;
  setAlert: (_a: ts.types.common.Alert) => void;
  setDefinition: (_d: ts.types.riskModel.DefinitionDraft & { noValidate?: boolean }) => void;
  setDefinitionChanged: (_dc: boolean) => void;
  setRiskModel: (_r: ts.types.riskModel.RiskModel) => void;
  setRightDrawer: (_v: ts.types.riskModel.RightDrawer) => void;
  rightDrawer: ts.types.riskModel.RightDrawer;
  universes: ts.types.universe.Universe[];
  updateDefinition: (_d?: ts.types.riskModel.DefinitionDraft, _ts?: boolean, _valid?: boolean) => Promise<void>;
  updateRiskModel: (_p: ts.types.riskModel.RiskModelDraft) => Promise<void>;
  user: ts.types.user.User;
  validating: boolean;
  validationDef: ts.types.common.ValidationErrors;
  organization: ts.types.organization.Organization;
};

const RiskModelContext = React.createContext<RiskModelContextTypes>(null);

const definitionFromRiskModel = (riskModel: ts.types.riskModel.RiskModelDraft): ts.types.riskModel.DefinitionDraft => {
  const definition = riskModel.definition;
  return {
    style: definition.style,
    categories: definition.categories,
    market: definition.market,
    version: definition.version,
    type: riskModel.definition.type || 'FINSERA',
    universe_id: definition.universe_id,
    industry_analysis_job_id: definition.industry_analysis_job_id,
    parameters: definition.parameters,
    publish_as_external: definition.publish_as_external,
  };
};

// This context provider is passed to any component requiring the context
const Provider = ({ id, children }: ProviderProps) => {
  const resources = useSelector((state) => state.resources);
  const organization = useSelector((state) => state.auth.organization);
  const user = useSelector((state) => state.auth.currentUser);
  const documentationOpen = useSelector((state) => state.ui.documentSidebarOpen);

  const universes = resources.universes;

  const dispatch = useDispatch();
  const setDocumentSidebarOpen = (o: boolean) => dispatch(actions.ui.setDocumentSidebarOpen(o));

  const [riskModel, setRiskModel] = React.useState<ts.types.riskModel.RiskModelDraft>({
    definition: null,
  });
  const [alert, setAlert] = React.useState<ts.types.common.Alert>();
  const [definition, setDefinition] = React.useState<ts.types.riskModel.DefinitionDraft & { noValidate?: boolean }>({});
  const [validationDef, setValidationDef] = React.useState<ts.types.common.ValidationErrors>();
  const [definitionChanged, setDefinitionChanged] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [uiMetadata, setUiMetadata] = React.useState<ts.types.riskModel.UiMetaData>();
  const [userPreferences, setUserPreferences] = React.useState<ts.types.riskModel.UserPreferences['preferences']>();
  const [rightDrawer, setRightDrawer] = React.useState<ts.types.riskModel.RightDrawer>(null);

  const { loadAndCreateAnalyses } = useAnalysesContext<
    ts.enums.RISK_MODEL_ANALYSIS_TYPE_ENUM,
    ts.types.riskModel.RiskModel
  >();

  const { keepCurrent: keepCurrentFn } = hooks.useKeepCurrent<ts.types.riskModel.RiskModel>({
    setAlert,
    setResource: setRiskModel,
    resourceType: 'risk_models',
    getApiParams: (rm) => ({
      universe_id: rm.definition?.universe_id || undefined,
    }),
  });

  const { load: loadUserPreferences, update: updateUserPreferences } = hooks.useUpdateUserPreferences<
    ts.types.riskModel.UserPreferences['preferences']
  >({
    resourceId: id,
    resourceType: ts.enums.RESOURCES_TYPES_ENUM.RISK_MODEL,
    setAlert,
    onSuccess: (prefs) => setUserPreferences(prefs),
  });

  const { loading: loadingResource } = hooks.useResourceById<ts.types.riskModel.RiskModel>({
    item_type: 'risk_models',
    id,
    setAlert,
    effects: [
      async (rm) => {
        setRiskModel(rm);
        setDefinition({ ...definitionFromRiskModel(rm) });
        setUiMetadata(rm.ui_metadata ?? ({} as any));
        if (rm.is_valid) setValidationDef({ valid: true });
        else if (!helpers.resourcesUtils.justCreated(rm.created_at))
          setValidationDef(utils.validateDefinition({ ...definitionFromRiskModel(rm) }));
        keepCurrentFn(rm);
      },
      async (rm) => await loadAndCreateAnalyses(rm.shared, rm),
      async () => await loadUserPreferences(),
    ],
  });

  // Archived deps
  const archivedDeps = React.useMemo(
    () => utils.definition.getArchivedDeps(riskModel as ts.types.riskModel.RiskModel, resources),
    [riskModel]
  );

  // Use hook to validate definition changes
  hooks.useDefinitionValidate<ts.types.riskModel.DefinitionDraft>({
    id,
    validate: () => {
      setValidationDef(utils.validateDefinition(definition));
      setDefinitionChanged(true);
    },
    definition,
    setValidating,
  });

  hooks.useEffectWithoutFirst(() => {
    if (documentationOpen && !!rightDrawer) setRightDrawer(null);
  }, [documentationOpen]);

  hooks.useEffectWithoutFirst(() => {
    if (!!rightDrawer && documentationOpen) setDocumentSidebarOpen(false);
  }, [rightDrawer]);

  const updateRiskModel = (newData: ts.types.riskModel.RiskModelDraft) =>
    utils.apiProcedures.updateRiskModel(id, newData, setRiskModel, (err) =>
      setAlert({ severity: ts.enums.ALERT_SEVERITY_ENUM.ERROR, message: err })
    );

  const updateDefinition = (
    def: ts.types.riskModel.DefinitionDraft = definition,
    updateDefTimestamp = true,
    isValid = validationDef?.valid ?? false
  ) =>
    utils.apiProcedures.updateDefinition(
      id,
      def,
      uiMetadata,
      isValid,
      (updatedRiskModel) => {
        setRiskModel(updatedRiskModel);
        setUiMetadata(updatedRiskModel.ui_metadata ?? ({} as any));
        setDefinitionChanged(false);
        // Reupdate definition in case it was triggered from the report framework
        setDefinition({
          ...def,
          noValidate: true,
        });
      },
      (err) => setAlert({ severity: ts.enums.ALERT_SEVERITY_ENUM.ERROR, message: err }),
      updateDefTimestamp
    );

  const updateUiMetadata = hooks.useUpdateUiMetadata<ts.types.riskModel.RiskModel, ts.types.riskModel.UiMetaData>({
    resourceId: id,
    resourceType: 'risk_models',
    resource: riskModel,
    setAlert,
    onSuccess: (updatedRiskModel) => {
      setRiskModel(updatedRiskModel);
      setUiMetadata(updatedRiskModel.ui_metadata ?? ({} as any));
    },
  });

  const isExternalRiskModel = _.isEmpty(definition?.parameters);

  const depsTimestamp = React.useMemo(() => {
    if (!riskModel) return [];
    return [
      riskModel,
      ...helpers.resourceGraph.getDeps(
        {
          ...(riskModel as ts.types.riskModel.RiskModel),
          resourceType: ts.enums.RESOURCES_TYPES_ENUM.RISK_MODEL,
        },
        resources
      ),
    ]
      .map((r) => ({
        dependency: r.id == riskModel.id ? 'Current Risk Model' : `${r.name} (${r.handle})`,
        timestamp: r.definition_updated_at,
      }))
      .filter((el) => !!el.timestamp);
  }, [riskModel, resources]);

  return (
    <RiskModelContext.Provider
      value={{
        alert,
        archivedDeps,
        definition,
        definitionChanged,
        depsTimestamp,
        isExternalRiskModel: isExternalRiskModel,
        keepCurrent: () => keepCurrentFn(riskModel as ts.types.riskModel.RiskModel, true),
        loading: loadingResource,
        resources,
        uiMetadata,
        updateUiMetadata,
        updateUserPreferences,
        userPreferences,
        riskModel,
        setAlert,
        setDefinition,
        setDefinitionChanged,
        setRiskModel,
        rightDrawer,
        setRightDrawer,
        universes,
        updateDefinition,
        updateRiskModel,
        user,
        validating,
        validationDef,
        organization,
      }}
    >
      {children}
    </RiskModelContext.Provider>
  );
};

const RiskModelContextProvider = Provider;

export { RiskModelContext };
export { RiskModelContextProvider };
